import { DOMAdapter } from '../../../environment/adapter.mjs';
import { ExtensionType } from '../../../extensions/Extensions.mjs';
import { warn } from '../../../utils/logging/warn.mjs';

"use strict";
class GpuDeviceSystem {
  /**
   * @param {WebGPURenderer} renderer - The renderer this System works for.
   */
  constructor(renderer) {
    this._renderer = renderer;
  }
  async init(options) {
    if (this._initPromise) return this._initPromise;
    this._options = options;
    this._initPromise = (options.gpu ? Promise.resolve(options.gpu) : this._createDeviceAndAdaptor(options)).then((gpu) => this._setGpu(gpu));
    return this._initPromise;
  }
  _setGpu(gpu) {
    this.gpu = gpu;
    this.extensions = {
      transientAttachment: typeof GPUTextureUsage.TRANSIENT_ATTACHMENT === "number"
    };
    if (!this._options.gpu) {
      void gpu.device.lost.then(() => this._restoreDevice()).catch((e) => warn("WebGPU device was lost and could not be restored", e));
    }
    this._renderer.runners.contextChange.emit(this.gpu);
  }
  async _restoreDevice() {
    if (!this._renderer) return;
    const gpu = await this._createDeviceAndAdaptor(this._options);
    if (!this._renderer) {
      gpu.device.destroy();
      return;
    }
    this._setGpu(gpu);
  }
  /**
   * Handle the context change event
   * @param gpu
   */
  contextChange(gpu) {
    this._renderer.gpu = gpu;
  }
  /**
   * Helper class to create a WebGL Context
   * @param {object} options - An options object that gets passed in to the canvas element containing the
   *    context attributes
   * @see https://developer.mozilla.org/en/docs/Web/API/HTMLCanvasElement/getContext
   * @returns {WebGLRenderingContext} the WebGL context
   */
  async _createDeviceAndAdaptor(options) {
    const adapter = await DOMAdapter.get().getNavigator().gpu.requestAdapter({
      powerPreference: options.powerPreference,
      forceFallbackAdapter: options.forceFallbackAdapter
    });
    if (!adapter) {
      throw new Error("WebGPU not supported. No GPU adapter was returned by navigator.gpu.requestAdapter().");
    }
    const requiredFeatures = [
      "texture-compression-bc",
      "texture-compression-astc",
      "texture-compression-etc2",
      "indirect-first-instance"
    ].filter((feature) => adapter.features.has(feature));
    const device = await adapter.requestDevice({
      requiredFeatures,
      requiredLimits: {
        maxSampledTexturesPerShaderStage: adapter.limits.maxSampledTexturesPerShaderStage,
        maxSamplersPerShaderStage: adapter.limits.maxSamplersPerShaderStage
      }
    });
    return { adapter, device };
  }
  destroy() {
    if (!this._options?.gpu) {
      this.gpu?.device.destroy();
    }
    this.gpu = null;
    this.extensions = null;
    this._renderer = null;
  }
}
/** @ignore */
GpuDeviceSystem.extension = {
  type: [
    ExtensionType.WebGPUSystem
  ],
  name: "device"
};
/** The default options for the GpuDeviceSystem. */
GpuDeviceSystem.defaultOptions = {
  /**
   * {@link WebGPUOptions.powerPreference}
   * @default default
   */
  powerPreference: void 0,
  /**
   * Force the use of the fallback adapter
   * @default false
   */
  forceFallbackAdapter: false
};

export { GpuDeviceSystem };
//# sourceMappingURL=GpuDeviceSystem.mjs.map
